import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import { Skeleton } from "@/components/ui/skeleton";
import { companyRegisters } from "@/lib/api/companyRegister";
import { Badge } from "@/components/ui/badge";
import { Globe } from "lucide-react";

export function CompanyRegisterSettings() {
  const { toast } = useToast();
  const { getSetting, updateSetting } = useSystemSettings();
  const [enabledServices, setEnabledServices] = useState<Record<string, boolean>>({});
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const enabledServicesMap: Record<string, boolean> = {};
        for (const provider of companyRegisters) {
          const settingKey = `enable_company_register_${provider.id}`;
          const isEnabled = await getSetting(settingKey, "true") === "true";
          enabledServicesMap[provider.id] = isEnabled;
        }
        setEnabledServices(enabledServicesMap);
      } catch (error) {
        console.error("Error loading company register settings:", error);
        toast({
          title: "Error",
          description: "Failed to load company register service settings.",
          variant: "destructive",
        });
      } finally {
        setIsInitializing(false);
      }
    };

    loadSettings();
  }, [getSetting, toast]);

  const handleToggle = async (providerId: string, enabled: boolean) => {
    try {
      setEnabledServices((prev) => ({
        ...prev,
        [providerId]: enabled,
      }));

      const settingKey = `enable_company_register_${providerId}`;
      const success = await updateSetting(settingKey, enabled.toString());

      if (success) {
        toast({
          title: "Setting updated",
          description: `${enabled ? "Enabled" : "Disabled"} ${providerId} company register service.`,
        });
      } else {
        setEnabledServices((prev) => ({
          ...prev,
          [providerId]: !enabled,
        }));
        toast({
          title: "Error",
          description: "Failed to update setting.",
          variant: "destructive",
        });
      }
    } catch (error) {
      setEnabledServices((prev) => ({
        ...prev,
        [providerId]: !enabled,
      }));
      console.error(`Error updating ${providerId} setting:`, error);
      toast({
        title: "Error",
        description: `Failed to update ${providerId} setting.`,
        variant: "destructive",
      });
    }
  };

  if (isInitializing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Company Register Services</CardTitle>
          <CardDescription>Loading settings...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between space-x-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-6 w-10" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company Register Services</CardTitle>
        <CardDescription>
          Enable or disable company register services that users can select from
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          {companyRegisters.map((provider) => (
            <div key={provider.id} className="flex items-center justify-between space-x-2 border-b pb-3 last:border-0 last:pb-0">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor={`provider-${provider.id}`} className="text-base font-medium">
                    {provider.name}
                  </Label>
                  <Badge variant="outline" className="ml-2">
                    <Globe className="mr-1 h-3 w-3" />
                    {provider.countryCode}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {provider.requiresApiKey
                    ? "Requires user API key configuration"
                    : "No API key required"}
                </p>
              </div>
              <Switch
                id={`provider-${provider.id}`}
                checked={enabledServices[provider.id] ?? true}
                onCheckedChange={(checked) => handleToggle(provider.id, checked)}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
